import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import StatusBadge from 'components/StatusBadge'
import React from 'react'
import { NavLink as RNavLink } from 'react-router-dom'
import { history } from 'store'
import { merchantTheme, merchantThemeT } from 'theme'

type colorsObj = merchantThemeT['colors']
type colorNames = keyof colorsObj

type IconPropsT = {
  className?: string
  fillColor?: string
}

// Fixed single-line row heights (default vs. left-nav) and the vertical
// padding a wrapped (multi-line) label needs so it doesn't sit flush against
// the row edges.
const NAV_LINK_HEIGHT_DEFAULT = '40px'
const NAV_LINK_HEIGHT_LEFT_NAV = '44px'
const WRAP_TEXT_VERTICAL_PADDING = '8px'

type TagWrapperPropsT = {
  className: string
  children: React.ReactNode
  tag?: string
  url?: string
  onClick?: VoidFunction
  style?: React.CSSProperties
  'aria-disabled'?: boolean
  tabIndex?: number
}
const TagWrapper: React.FC<TagWrapperPropsT> = props => {
  const {
    children,
    className,
    tag = 'div',
    url,
    onClick,
    style,
    'aria-disabled': ariaDisabled,
    tabIndex,
  } = props

  if (tag === 'a') {
    return (
      <a
        className={className}
        href={url}
        style={style}
        aria-disabled={ariaDisabled}
        tabIndex={tabIndex}
      >
        {children}
      </a>
    )
  }
  // For onClick-only NavLinks (no `link` prop), expose the same keyboard
  // semantics as a real button so Enter/Space activate it.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  }
  return (
    <div
      tabIndex={tabIndex ?? 0}
      role="button"
      className={className}
      style={style}
      aria-disabled={ariaDisabled}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  )
}

type MainPropsT = {
  text: string
  colorName?: colorNames
  link?: string
  Icon?: React.FC<IconPropsT> | string
  status?: 'coming_soon' | 'beta' | 'new'
  disabled?: boolean
  disabledReason?: string
  tooltipPlacement?: 'top' | 'right' | 'bottom' | 'left'
  url?: string
  onClick?: VoidFunction
  textClassName?: string
  isLegacy?: boolean
  isLeftNav?: boolean
  RightIcon?: React.FC<IconPropsT> | string
  // Opt-in: wraps long text onto multiple lines instead of the default
  // single-line truncation/overflow. Used for user-generated labels (e.g.
  // saved favorite names) that can run long, without changing the fixed-
  // height single-line layout every other nav link relies on.
  wrapText?: boolean
  // Opt-in: suppresses the "active route" highlight even when `link` matches
  // the current pathname. `isActive` below only compares pathname, ignoring
  // query string, so a link like `/insights_lab` (no query) reads as active
  // on every query-string variant of that route -- appropriate for a plain
  // section link, wrong for a link that's meant to look like its sibling
  // list items (e.g. the favorites-overflow "+N more" link, which should
  // always match the other favorite links' default styling).
  disableActiveHighlight?: boolean
  // Opt-in: renders RightIcon immediately after the text with a small gap,
  // instead of the default `ml-auto` wrapper that pushes it to the row's far
  // edge. Default false preserves today's far-edge placement for every
  // existing caller (e.g. the legacy full-width "Thanx Insights Lab" footer
  // link, which wants that banner-style far-edge arrow) -- only opt in for a
  // link whose RightIcon should sit right after its text (e.g. "Browse all
  // reports").
  rightIconInline?: boolean
  // Opt-in: renders the label with Text.SmallCaps3 instead of the default
  // Text.BodyText4, matching the visual weight of the sibling SectionHeader
  // labels (e.g. "MANAGE CUSTOMERS", "REVENUE") which render via
  // Text.SmallCaps3 in SectionHeader.tsx. Default false preserves today's
  // regular-case text for every existing caller -- only opt in for a link
  // that's meant to read with section-header weight (e.g. "Browse all
  // reports").
  uppercase?: boolean
}
// Extracted so its logic is unit-testable directly: css()-driven styles (like
// the active/hover background below) don't render in jsdom without a real
// Styletron engine attached, so asserting on rendered className/computed
// style can't verify this. See NavLink.test.tsx.
export function isActiveLink(
  pathname: string,
  link: string | undefined,
  disableActiveHighlight: boolean
): boolean {
  if (disableActiveHighlight) return false
  return pathname === link?.split('#')[0]
}

const NavLink: React.FC<MainPropsT> = props => {
  const {
    colorName = 'white',
    text,
    link,
    Icon,
    status,
    disabled = false,
    disabledReason,
    tooltipPlacement,
    url,
    onClick,
    isLegacy = false,
    isLeftNav = false,
    RightIcon,
    wrapText = false,
    disableActiveHighlight = false,
    rightIconInline = false,
    uppercase = false,
  } = props
  const [css] = useStyletron()
  const isActive = isActiveLink(
    history.location.pathname,
    link,
    disableActiveHighlight
  )
  const LinkComponent = !!link ? RNavLink : TagWrapper
  // Legacy rows read as muted text now, not a shaded background (see the
  // bordered-card wrapper their callers render around the whole group) — so
  // they take the same grey50 a disabled row already used.
  const color =
    disabled || isLegacy
      ? merchantTheme.colors['grey50']
      : merchantTheme.colors['grey90']
  const xPadding = isLeftNav ? 'px-m' : 'px-s'
  let backgroundColor
  let hoverFocusBackgroundColor
  if (disabled) {
    backgroundColor = merchantTheme.colors[colorName]
    hoverFocusBackgroundColor = merchantTheme.colors[colorName]
  } else if (isActive) {
    backgroundColor = merchantTheme.colors.belizeHole10
    hoverFocusBackgroundColor = merchantTheme.colors.grey10
  } else {
    backgroundColor = merchantTheme.colors[colorName]
    hoverFocusBackgroundColor = merchantTheme.colors.grey10
  }
  const rowHeight = isLeftNav
    ? NAV_LINK_HEIGHT_LEFT_NAV
    : NAV_LINK_HEIGHT_DEFAULT

  const childProps: {
    className: string
    activeClassName?: string
    to?: string
    url?: string
    tag?: string
    onClick?: VoidFunction
    disabled?: boolean
    'aria-disabled'?: boolean
    tabIndex?: number
    style?: React.CSSProperties
  } = {
    onClick: disabled
      ? undefined
      : () => {
          const activeElement = document.activeElement as HTMLElement
          if (activeElement) activeElement.blur()
          onClick?.()
        },
    ...(disabled && { 'aria-disabled': true, tabIndex: -1 }),
    // Plain static values (no pseudo-selectors needed), set as inline style
    // rather than through styletron's css() so they're directly assertable in
    // tests via toHaveStyle — jsdom doesn't resolve styletron-generated
    // classes back into computed style. Same precedent as Section.tsx,
    // SampleCustomerRow.tsx, and LogoBadge.tsx elsewhere in this app.
    style: {
      whiteSpace: wrapText ? 'normal' : 'nowrap',
      // Fixed single-line height doesn't fit wrapped multi-line text — switch
      // to auto (with a min-height floor) so a wrapped label can grow the
      // row instead of being clipped.
      height: wrapText ? 'auto' : rowHeight,
      minHeight: rowHeight,
      paddingTop: wrapText ? WRAP_TEXT_VERTICAL_PADDING : 0,
      paddingBottom: wrapText ? WRAP_TEXT_VERTICAL_PADDING : 0,
    },
    className: `align-items-center d-flex ${css({
      pointerEvents: disabled ? 'none' : 'inherit',
    })} ${css({
      color: color,
      backgroundColor,
      cursor: disabled ? 'default' : 'pointer',
      ':hover': {
        backgroundColor: hoverFocusBackgroundColor,
        textDecoration: 'none',
      },
      ':focus': {
        backgroundColor: hoverFocusBackgroundColor,
        textDecoration: 'none',
      },
    })} ${xPadding}`,
  }

  if (!!link) {
    childProps.to = disabled ? '' : link
    childProps.disabled = disabled
  }
  if (!!url) {
    childProps.url = disabled ? '' : url
    childProps.tag = 'a'
  }

  // Text.SmallCaps3 accepts the same props (tag/color/bold/className) as
  // Text.BodyText4 -- see NavLink's `uppercase` prop doc above -- so it can
  // be swapped in without touching the surrounding layout.
  const TextComponent = uppercase ? Text.SmallCaps3 : Text.BodyText4

  const content = (
    // @ts-ignore
    <LinkComponent {...childProps}>
      {Icon && <Icon className="mr-xs" fillColor={color} />}
      <TextComponent
        tag="span"
        color={disabled || isLegacy ? 'grey50' : 'grey90'}
        // Bold only when uppercase, to match SectionHeader's
        // `<Text.SmallCaps3 bold>` weight -- BodyText4 callers are
        // unaffected since uppercase defaults false.
        bold={uppercase}
        // Flex items default to `min-width: auto`, which lets a span's
        // intrinsic (single-line) content width push past its container
        // instead of wrapping — override it so long favorite names actually
        // wrap rather than overflow. overflowWrap/wordBreak additionally
        // cover a single unbroken "word" longer than the container.
        className={
          wrapText
            ? css({
                minWidth: 0,
                flex: '1 1 auto',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
              })
            : undefined
        }
      >
        {text}
      </TextComponent>
      {status && <StatusBadge type={status} />}
      {RightIcon && rightIconInline && (
        <RightIcon className="ml-xs" fillColor={color} />
      )}
      {RightIcon && !rightIconInline && (
        <div className="d-flex justify-content-end ml-auto">
          <RightIcon className="ml-xs" fillColor={color} />
        </div>
      )}
    </LinkComponent>
  )

  return (
    <PermissionTooltip
      disabled={disabled}
      reason={disabledReason}
      id={`nav-perm-${(link ?? url ?? text).replace(/[^a-z0-9_-]/gi, '-')}`}
      placement={tooltipPlacement}
    >
      {content}
    </PermissionTooltip>
  )
}

export default NavLink
